import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X, Loader2, Star, Plus, CreditCard, MapPin, Check, ShoppingCart, Wallet } from 'lucide-react';
import api from '../api/client';
import { toast } from 'sonner';
import PostcodeAddressPicker from './PostcodeAddressPicker';
import { useSettings } from '../context/SettingsContext';
import { useCart } from '../context/CartContext';
import { usePricing } from '../context/PricingContext';
import { resolveImage } from './admin/ImageUpload';
import { vatAmount, computeDelivery } from '../utils/pricing';

const CheckoutDialog = ({ open, onClose, onSuccess, subtotal = 0, promoCode = null }) => {
  const [step, setStep] = useState('address');
  const [addresses, setAddresses] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [mode, setMode] = useState('list');
  const [newAddress, setNewAddress] = useState(null);
  const [saveAddress, setSaveAddress] = useState(true);
  const [payment, setPayment] = useState({ card: '', exp: '', cvc: '', name: '' });
  const [paymentMethod, setPaymentMethod] = useState('instant');
  const [newsletterOptIn, setNewsletterOptIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recommendations, setRecommendations] = useState([]);
  const { settings } = useSettings();
  const { addItem, items: cartItems } = useCart();
  const { creditLimit, paymentMethods } = usePricing() || { creditLimit: 0, paymentMethods: ['instant'] };
  const vatRate = settings?.vat_rate ?? 20;
  const recIds = settings?.checkout_recommendations || [];
  const creditEnabled = paymentMethods?.includes('credit');

  useEffect(() => {
    if (!open) return;
    setStep('address'); setNewAddress(null); setPaymentMethod('instant');
    api.get('/addresses').then(r => {
      setAddresses(r.data);
      if (r.data.length > 0) {
        const fav = r.data.find(a => a.is_favorite);
        setSelectedId(fav?.id || r.data[0].id);
        setMode('list');
      } else {
        setMode('new');
      }
    }).catch(() => setMode('new'));
    // Load recommendations
    if (recIds.length > 0) {
      Promise.all(recIds.slice(0, 5).map(id => api.get(`/products/${id}`).then(r => r.data).catch(() => null)))
        .then(arr => setRecommendations(arr.filter(Boolean)));
    } else { setRecommendations([]); }
  }, [open, JSON.stringify(recIds)]);

  if (!open) return null;

  const freeThreshold = Number(settings?.free_delivery_threshold ?? 75);
  const standardFee = Number(settings?.standard_delivery_fee ?? 4.95);
  const delivery = computeDelivery(cartItems || [], { threshold: freeThreshold, flatRate: standardFee }).total;
  const vat = vatAmount(subtotal + delivery, vatRate);
  const total = +(subtotal + delivery + vat).toFixed(2);

  const canContinueToPayment = () => {
    if (mode === 'list') return !!selectedId;
    return !!(newAddress && newAddress.line1 && newAddress.city && newAddress.postcode && newAddress.name);
  };

  const placeOrder = async () => {
    setLoading(true);
    try {
      const basePayload = mode === 'list' && selectedId
        ? { address_id: selectedId }
        : { address: newAddress, save_address: saveAddress };

      if (paymentMethod === 'instant') {
        // Card payment via Stripe Checkout — build session on backend and redirect.
        const r = await api.post('/payments/stripe/checkout', {
          ...basePayload,
          newsletter_opt_in: newsletterOptIn,
          promo_code: promoCode || undefined,
          origin_url: window.location.origin,
        });
        if (r.data?.checkout_url) {
          window.location.assign(r.data.checkout_url);
          return; // Stripe will bring us back to /payment/success
        }
        toast.error('Payment gateway did not return a checkout URL');
      } else {
        // Credit purchase — no gateway call, admin reconciles offline
        const res = await api.post('/orders', { ...basePayload, payment_method: 'credit', newsletter_opt_in: newsletterOptIn, promo_code: promoCode || undefined });
        toast.success(`Order #${res.data.id.slice(0, 8)} placed on credit`);
        onSuccess(res.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to place order');
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-sm w-full max-w-xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-neutral-200 sticky top-0 bg-white z-10">
          <h2 className="text-lg font-bold text-[#12263A]">Checkout</h2>
          <button onClick={onClose} aria-label="Close" className="p-1 hover:bg-neutral-100 rounded"><X className="w-5 h-5" /></button>
        </div>

        {/* Stepper */}
        <div className="flex px-5 py-3 gap-2 border-b border-neutral-100 bg-neutral-50">
          <div className={`flex items-center gap-2 px-3 py-1 rounded-sm text-xs font-bold ${step === 'address' ? 'bg-[#F46A25] text-white' : 'bg-white text-neutral-600 border border-neutral-200'}`}>
            <MapPin className="w-3.5 h-3.5" /> 1. Address {step === 'payment' && <Check className="w-3.5 h-3.5" />}
          </div>
          <div className={`flex items-center gap-2 px-3 py-1 rounded-sm text-xs font-bold ${step === 'payment' ? 'bg-[#F46A25] text-white' : 'bg-white text-neutral-500 border border-neutral-200'}`}>
            <CreditCard className="w-3.5 h-3.5" /> 2. Payment
          </div>
          <div className={`ml-auto text-xs text-neutral-600 self-center text-right`}>
            <div>Subtotal (ex VAT): <b className="text-[#12263A]">£{subtotal.toFixed(2)}</b></div>
            <div>VAT ({vatRate}%): <b className="text-[#12263A]">£{vat.toFixed(2)}</b></div>
            <div>Total: <b className="text-[#F46A25] text-sm">£{total.toFixed(2)}</b></div>
          </div>
        </div>

        {recommendations.length > 0 && step === 'address' && (
          <div className="px-5 pt-3">
            <div className="bg-gradient-to-r from-[#FFF6EF] to-orange-50 border border-orange-200 rounded-sm p-3">
              <div className="text-xs font-bold text-[#F46A25] uppercase mb-2 flex items-center gap-1"><Star className="w-3 h-3 fill-[#F46A25]" /> You might also need</div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                {recommendations.map(p => (
                  <div key={p.id} className="bg-white border border-neutral-200 rounded-sm p-2 flex flex-col">
                    <img src={resolveImage(p.image)} alt={p.name} className="w-full aspect-square object-cover rounded-sm mb-1" />
                    <div className="text-[10px] text-neutral-600 line-clamp-2 mb-1 min-h-[24px]">{p.name}</div>
                    <div className="text-xs font-bold text-[#12263A] mb-1">£{p.price.toFixed(2)}</div>
                    <button
                      onClick={() => { addItem(p, 1); toast.success('Added'); }}
                      className="text-[10px] bg-[#F46A25] hover:bg-[#e05a15] text-white font-bold py-1 rounded-sm flex items-center justify-center gap-1"
                    ><Plus className="w-3 h-3" /> Add</button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {step === 'address' && (
          <div className="p-5 space-y-3">
            {addresses.length > 0 && (
              <div className="flex gap-2 border-b border-neutral-200 pb-3 mb-1">
                <button type="button" onClick={() => setMode('list')} className={`text-sm font-semibold px-3 py-1.5 rounded-sm ${mode === 'list' ? 'bg-[#F46A25] text-white' : 'bg-neutral-100 text-[#12263A]'}`}>Saved ({addresses.length})</button>
                <button type="button" onClick={() => setMode('new')} className={`text-sm font-semibold px-3 py-1.5 rounded-sm flex items-center gap-1 ${mode === 'new' ? 'bg-[#F46A25] text-white' : 'bg-neutral-100 text-[#12263A]'}`}><Plus className="w-3.5 h-3.5" /> New address</button>
              </div>
            )}

            {mode === 'list' && addresses.length > 0 && (
              <div className="space-y-2">
                {addresses.map(a => (
                  <label key={a.id} className={`block border-2 ${selectedId === a.id ? 'border-[#F46A25] bg-orange-50' : 'border-neutral-200'} rounded-sm p-3 cursor-pointer hover:border-orange-300`}>
                    <div className="flex items-start gap-2">
                      <input type="radio" name="addr" checked={selectedId === a.id} onChange={() => setSelectedId(a.id)} className="mt-1 accent-[#F46A25]" />
                      <div className="flex-1 text-sm">
                        <div className="flex items-center gap-2 mb-0.5">
                          {a.label && <span className="text-[10px] uppercase font-bold text-[#F46A25]">{a.label}</span>}
                          {a.is_favorite && <span className="text-[10px] bg-[#F46A25] text-white font-bold px-1.5 py-0.5 rounded-sm flex items-center gap-0.5"><Star className="w-2.5 h-2.5 fill-white" /> Favorite</span>}
                        </div>
                        <div className="font-semibold text-[#12263A]">{a.name}</div>
                        <div className="text-neutral-700">{a.line1}{a.line2 ? `, ${a.line2}` : ''}</div>
                        <div className="text-neutral-700">{a.city}, {a.postcode}</div>
                        {a.phone && <div className="text-xs text-neutral-500 mt-0.5">☎ {a.phone}</div>}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}

            {mode === 'new' && (
              <div className="space-y-3">
                <PostcodeAddressPicker onSelect={setNewAddress} />
                <label className="flex items-center gap-2 text-sm cursor-pointer pt-1">
                  <input type="checkbox" checked={saveAddress} onChange={e => setSaveAddress(e.target.checked)} className="accent-[#F46A25]" />
                  Save this address to my address book
                </label>
              </div>
            )}

            <div className="pt-2 flex gap-2">
              <button type="button" onClick={onClose} className="flex-1 border border-neutral-300 rounded-sm py-2.5 text-sm font-semibold hover:bg-neutral-100">Cancel</button>
              <button
                type="button"
                onClick={() => setStep('payment')}
                disabled={!canContinueToPayment()}
                className="flex-1 bg-[#F46A25] hover:bg-[#e05a15] text-white font-bold py-2.5 rounded-sm text-sm disabled:opacity-60 flex items-center justify-center gap-2"
              >
                Continue to payment <CreditCard className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {step === 'payment' && (
          <div className="p-5 space-y-3">
            {creditEnabled && (
              <div className="border border-neutral-200 rounded-sm p-3">
                <div className="text-xs font-semibold uppercase text-[#12263A] mb-2">Payment method</div>
                <div className="grid grid-cols-2 gap-2">
                  <label
                    data-testid="payment-method-instant"
                    className={`cursor-pointer border-2 rounded-sm p-3 flex items-start gap-2 transition-colors ${paymentMethod === 'instant' ? 'border-[#F46A25] bg-orange-50' : 'border-neutral-200 hover:border-orange-300'}`}
                  >
                    <input
                      type="radio"
                      name="pm"
                      checked={paymentMethod === 'instant'}
                      onChange={() => setPaymentMethod('instant')}
                      className="mt-0.5 accent-[#F46A25]"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-1.5 text-sm font-bold text-[#12263A]"><CreditCard className="w-4 h-4" /> Pay by card</div>
                      <div className="text-[11px] text-neutral-600">Secure Stripe checkout</div>
                    </div>
                  </label>
                  <label
                    data-testid="payment-method-credit"
                    className={`cursor-pointer border-2 rounded-sm p-3 flex items-start gap-2 transition-colors ${paymentMethod === 'credit' ? 'border-[#F46A25] bg-orange-50' : 'border-neutral-200 hover:border-orange-300'}`}
                  >
                    <input
                      type="radio"
                      name="pm"
                      checked={paymentMethod === 'credit'}
                      onChange={() => setPaymentMethod('credit')}
                      className="mt-0.5 accent-[#F46A25]"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-1.5 text-sm font-bold text-[#12263A]"><Wallet className="w-4 h-4" /> Pay on credit</div>
                      <div className="text-[11px] text-neutral-600">Credit limit £{Number(creditLimit || 0).toFixed(2)}</div>
                    </div>
                  </label>
                </div>
              </div>
            )}

            {paymentMethod === 'instant' && (
              <div className="bg-orange-50 border border-orange-200 rounded-sm p-4 text-sm text-[#12263A] space-y-2" data-testid="stripe-checkout-info">
                <div className="flex items-center gap-2 font-bold"><CreditCard className="w-4 h-4 text-[#F46A25]" /> Secure Stripe checkout</div>
                <p className="text-xs text-neutral-700">
                  Clicking <b>Pay £{total.toFixed(2)}</b> will take you to Stripe's hosted card
                  form. Your card details never touch our server. Once payment succeeds
                  we'll bring you back to the order confirmation page.
                </p>
                <p className="text-[11px] text-neutral-500">
                  Test card: <code className="font-mono">4242 4242 4242 4242</code>, any future
                  expiry, any CVC.
                </p>
              </div>
            )}

            {paymentMethod === 'credit' && (
              <div className="bg-[#FFF6EF] border border-[#F46A25]/30 rounded-sm p-3 text-sm text-[#12263A]">
                <div className="flex items-center gap-2 font-bold mb-1"><Wallet className="w-4 h-4 text-[#F46A25]" /> This order will be placed on credit</div>
                <p className="text-xs text-neutral-700 mb-1">Available credit line: <b>£{Number(creditLimit || 0).toFixed(2)}</b>. You&apos;ll receive an invoice for £{total.toFixed(2)} on the agreed terms.</p>
                <p className="text-[11px] text-neutral-500">No card details required.</p>
              </div>
            )}

            <div className="border-t border-neutral-200 pt-3 space-y-1 text-sm">
              <div className="flex justify-between text-neutral-600"><span>Subtotal (ex VAT)</span><span>£{subtotal.toFixed(2)}</span></div>
              <div className="flex justify-between text-neutral-600"><span>Delivery</span><span>{delivery === 0 ? 'FREE' : `£${delivery.toFixed(2)}`}</span></div>
              <div className="flex justify-between text-neutral-600"><span>VAT ({vatRate}%)</span><span>£{vat.toFixed(2)}</span></div>
              <div className="flex justify-between items-baseline pt-2 border-t border-neutral-100">
                <span className="text-sm font-bold text-[#12263A]">Total to pay</span>
                <span className="text-2xl font-black text-[#12263A]">£{total.toFixed(2)}</span>
              </div>
            </div>

            <label className="flex items-start gap-2 cursor-pointer select-none pt-1" data-testid="checkout-newsletter-wrap">
              <input
                type="checkbox"
                checked={newsletterOptIn}
                onChange={e => setNewsletterOptIn(e.target.checked)}
                data-testid="checkout-newsletter-checkbox"
                className="mt-0.5 w-4 h-4 accent-[#F46A25]"
              />
              <span className="text-xs text-neutral-700 leading-relaxed">
                Send me offers, product updates and Dr Tools trade newsletters.
              </span>
            </label>

            <div className="pt-2 flex gap-2">
              <button type="button" onClick={() => setStep('address')} className="flex-1 border border-neutral-300 rounded-sm py-2.5 text-sm font-semibold hover:bg-neutral-100">← Back</button>
              <button
                type="button"
                onClick={placeOrder}
                disabled={loading}
                data-testid="checkout-place-order"
                className="flex-1 bg-[#F46A25] hover:bg-[#e05a15] text-white font-bold py-2.5 rounded-sm text-sm disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : paymentMethod === 'credit' ? (<><Wallet className="w-4 h-4" /> Place order on credit · £{total.toFixed(2)}</>) : (<><CreditCard className="w-4 h-4" /> Pay £{total.toFixed(2)}</>)}
              </button>
            </div>
            <p className="text-[11px] text-neutral-500 text-center">Secure checkout · SSL encrypted</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckoutDialog;
